import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CheckCircle,
  ClockCounterClockwise,
  Play,
  Sparkle,
  StopCircle,
  Warning,
} from "@phosphor-icons/react";
import { useMutation, useQuery } from "convex/react";
import { api as convexApi } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import type {
  PromptReplayCheckpointDetail,
  PromptReplayCheckpointSummary,
  PromptReplayRunDetail,
  PromptReplaySessionSummary,
  PromptReplayStartResult,
} from "../../shared/promptReplay/types.ts";
import {
  notificationCopy,
  replayStartedCopy,
  replayStoppedCopy,
} from "../lib/notificationCopy.ts";
import { notify } from "../lib/notify.ts";
import { usePromptAdminContext } from "./PromptAdminShell.tsx";

type PromptSource = "draft" | "saved";

const SUPPORTED_WORKFLOW = "moderator_suggestions";

function formatSelectionMethod(
  selectionMethod: PromptReplayCheckpointDetail["selectionMethod"],
): string {
  switch (selectionMethod) {
    case "deterministic":
      return "auto";
    case "deterministic_fallback":
      return "judge fallback";
    case "judge":
      return "judge";
    case "none":
      return "none";
  }
}

export function PromptReplayPage() {
  const { auth, selectedTemplate, selectedDraft, selectedDirty } = usePromptAdminContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessions = useQuery(convexApi.promptReplay.listSessions, { auth }) as PromptReplaySessionSummary[] | undefined;
  const startReplay = useMutation(convexApi.promptReplay.startModeratorSuggestionsRun);
  const cancelReplay = useMutation(convexApi.promptReplay.cancelRun);
  const [promptSource, setPromptSource] = useState<PromptSource>("draft");
  const [judgeEnabled, setJudgeEnabled] = useState(false);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);

  const supported = selectedTemplate.workflow === SUPPORTED_WORKFLOW;
  const sessionId = searchParams.get("session") ?? "";
  const runId = searchParams.get("run") ?? "";
  const selectedCheckpointId = searchParams.get("checkpoint") ?? "";
  const selectedSession = sessions?.find((session) => session.id === sessionId) ?? null;
  const latestRun = useQuery(
    convexApi.promptReplay.getLatestRunForPromptAndSession,
    supported && selectedSession
      ? {
          auth,
          workflow: selectedTemplate.workflow,
          sessionId: selectedSession.id as Id<"sessions">,
        }
      : "skip",
  ) as PromptReplayRunDetail | null | undefined;
  const explicitRun = useQuery(
    convexApi.promptReplay.getRun,
    supported && runId
      ? {
          auth,
          runId: runId as Id<"promptReplayRuns">,
        }
      : "skip",
  ) as PromptReplayRunDetail | null | undefined;
  const activeRun = runId ? explicitRun : latestRun;
  const checkpoints = useQuery(
    convexApi.promptReplay.listCheckpointSummaries,
    activeRun
      ? {
          auth,
          runId: activeRun.id as Id<"promptReplayRuns">,
        }
      : "skip",
  ) as PromptReplayCheckpointSummary[] | undefined;
  const selectedCheckpointSummary =
    checkpoints?.find((checkpoint) => checkpoint.id === selectedCheckpointId) ??
    checkpoints?.[0] ??
    null;
  const selectedCheckpoint = useQuery(
    convexApi.promptReplay.getCheckpointDetail,
    selectedCheckpointSummary
      ? {
          auth,
          checkpointId: selectedCheckpointSummary.id as Id<"promptReplayCheckpoints">,
        }
      : "skip",
  ) as PromptReplayCheckpointDetail | null | undefined;
  const activeRunCanStop = activeRun?.status === "queued" || activeRun?.status === "running";
  const replayControlsLocked = starting || stopping || activeRunCanStop;

  useEffect(() => {
    if (!sessions?.length) {
      if (sessionId) {
        setSearchParams((current) => {
          const next = new URLSearchParams(current);
          next.delete("session");
          next.delete("run");
          next.delete("checkpoint");
          return next;
        }, { replace: true });
      }
      return;
    }

    if (sessionId && sessions.some((session) => session.id === sessionId)) return;
    const transcriptSession = sessions.find((session) => (session.transcriptEntryCount ?? 0) > 0) ?? sessions[0];
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("session", transcriptSession.id);
      next.delete("run");
      next.delete("checkpoint");
      return next;
    }, { replace: true });
  }, [sessionId, sessions, setSearchParams]);

  useEffect(() => {
    if (runId || !latestRun?.id) return;
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("run", latestRun.id);
      return next;
    }, { replace: true });
  }, [latestRun?.id, runId, setSearchParams]);

  useEffect(() => {
    if (!activeRun || !checkpoints?.length) return;
    if (selectedCheckpointId && checkpoints.some((checkpoint) => checkpoint.id === selectedCheckpointId)) return;
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (next.get("run") !== activeRun.id) return current;
      next.set("checkpoint", checkpoints[0].id);
      return next;
    }, { replace: true });
  }, [activeRun, checkpoints, selectedCheckpointId, setSearchParams]);

  function updateReplaySearch(updates: Record<string, string | null>, replace = false) {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null) next.delete(key);
        else next.set(key, value);
      }
      return next;
    }, { replace });
  }

  async function run() {
    if (!supported || !selectedSession || replayControlsLocked) return;
    setStarting(true);
    try {
      const request = {
        auth,
        sessionId: selectedSession.id as Id<"sessions">,
        workflow: selectedTemplate.workflow,
        provider: "groq" as const,
        judgeEnabled,
        ...(promptSource === "draft"
          ? { promptOverrides: [{ key: selectedTemplate.key, body: selectedDraft }] }
          : {}),
      };
      const started = await startReplay(request) as PromptReplayStartResult;
      updateReplaySearch({
        session: selectedSession.id,
        run: started.runId,
        checkpoint: null,
      });
      notify.success(replayStartedCopy({
        sessionTitle: selectedSession.title,
        promptSource,
        judgeEnabled,
      }));
    } catch {
      notify.error(notificationCopy.admin.couldNotStartReplay);
    } finally {
      setStarting(false);
    }
  }

  async function stop() {
    if (!activeRun || !activeRunCanStop || stopping) return;
    setStopping(true);
    try {
      await cancelReplay({
        auth,
        runId: activeRun.id as Id<"promptReplayRuns">,
      });
      notify.success(replayStoppedCopy({
        sessionTitle: selectedSession?.title ?? activeRun.sessionId,
      }));
    } catch {
      notify.error(notificationCopy.admin.couldNotStopReplay);
    } finally {
      setStopping(false);
    }
  }

  if (!supported) {
    return (
      <div className="prompt-replay" data-testid="prompt-replay-unsupported">
        <div className="prompt-editor-head">
          <div>
            <p className="prompt-key">{selectedTemplate.workflow}/replay</p>
            <h1>Replay Lab</h1>
            <p>
              Replay is currently wired to Moderator Suggestions, where answer-block checkpoints are deterministic.
            </p>
          </div>
          <span className="prompt-state default">not wired</span>
        </div>
        <div className="prompt-replay-empty">
          <Warning aria-hidden="true" weight="fill" />
          <span>{selectedTemplate.title} does not have a transcript replay harness yet.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="prompt-replay" data-testid="prompt-replay">
      <div className="prompt-editor-head">
        <div>
          <p className="prompt-key">{selectedTemplate.workflow}/replay</p>
          <h1>Replay Lab</h1>
          <p>
            Run this prompt against past transcripts using the same answer-block checkpoint logic as live sessions.
          </p>
        </div>
        <div className="prompt-replay-stat-strip" aria-label="Replay state">
          <span className="prompt-state custom">
            {promptSource === "draft" && selectedDirty ? "draft override" : "saved prompt"}
          </span>
          <span className="prompt-state default">Groq live LLM</span>
        </div>
      </div>

      <section className="prompt-replay-form" aria-label="Replay controls">
        <label>
          <span>Transcript session</span>
          <select
            data-testid="prompt-replay-session"
            value={sessionId}
            disabled={!sessions?.length || replayControlsLocked}
            onChange={(event) => updateReplaySearch({
              session: event.target.value,
              run: null,
              checkpoint: null,
            })}
          >
            {!sessions?.length && <option value="">No sessions found</option>}
            {sessions?.map((session) => (
              <option key={session.id} value={session.id}>
                {session.title} · {formatTranscriptCount(session)}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span>Prompt body</span>
          <select
            data-testid="prompt-replay-source"
            value={promptSource}
            disabled={replayControlsLocked}
            onChange={(event) => setPromptSource(event.target.value as PromptSource)}
          >
            <option value="draft">Current draft in editor</option>
            <option value="saved">Saved template</option>
          </select>
        </label>

        <label className="prompt-replay-provider-badge">
          <span>Provider</span>
          <strong>Groq live LLM</strong>
        </label>

        <label className="switch prompt-replay-switch">
          <input
            data-testid="prompt-replay-judge"
            type="checkbox"
            checked={judgeEnabled}
            disabled={replayControlsLocked}
            onChange={(event) => setJudgeEnabled(event.target.checked)}
          />
          Run Groq judge
        </label>

        <div className="prompt-replay-actions">
          <button
            className="btn primary"
            data-testid="prompt-replay-run"
            type="button"
            disabled={!selectedSession || replayControlsLocked}
            onClick={() => void run()}
          >
            <Play aria-hidden="true" weight="bold" />
            {starting ? "Starting..." : activeRunCanStop ? "Running..." : "Run Replay"}
          </button>

          {(activeRunCanStop || stopping) && (
            <button
              className="btn danger"
              data-testid="prompt-replay-stop"
              type="button"
              disabled={!activeRunCanStop || stopping}
              onClick={() => void stop()}
            >
              <StopCircle aria-hidden="true" weight="bold" />
              {stopping ? "Stopping..." : "Stop Replay"}
            </button>
          )}
        </div>
      </section>

      {selectedSession && (
        <div className="prompt-replay-context">
          <ClockCounterClockwise aria-hidden="true" weight="bold" />
          <span>
            Selected transcript: {selectedSession.kind}, {selectedSession.status}, started{" "}
            {new Date(selectedSession.startedAt).toLocaleString()}
          </span>
        </div>
      )}

      {activeRun ? (
        <ReplayLiveResult
          run={activeRun}
          checkpoints={checkpoints}
          selectedCheckpoint={selectedCheckpoint}
          selectedCheckpointId={selectedCheckpointSummary?.id ?? null}
          onSelectCheckpoint={(checkpointId) => updateReplaySearch({ checkpoint: checkpointId })}
        />
      ) : (
        <div className="prompt-replay-empty">
          <Sparkle aria-hidden="true" weight="fill" />
          <span>No replay run yet for this prompt route.</span>
        </div>
      )}
    </div>
  );
}

function ReplayLiveResult({
  run,
  checkpoints,
  selectedCheckpoint,
  selectedCheckpointId,
  onSelectCheckpoint,
}: {
  run: PromptReplayRunDetail;
  checkpoints: PromptReplayCheckpointSummary[] | undefined;
  selectedCheckpoint: PromptReplayCheckpointDetail | null | undefined;
  selectedCheckpointId: string | null;
  onSelectCheckpoint: (id: string) => void;
}) {
  const checkpointRows = checkpoints ?? [];
  const running = run.status === "queued" || run.status === "running";
  const cancelled = run.status === "cancelled";
  return (
    <section className="prompt-replay-results" data-testid="prompt-replay-results">
      <div className="prompt-replay-summary">
        <Metric label="Status" value={run.status} tone={run.status === "failed" || cancelled ? "warn" : running ? "live" : "default"} />
        <Metric label="Transcript processed" value={`${run.processedEntryCount}/${run.transcriptEntryCount}`} />
        <Metric label="Generated questions" value={String(run.selectedQuestionCount)} />
        <Metric label="Checkpoints" value={String(run.checkpointCount)} />
      </div>

      {run.error && (
        <div className="prompt-message warn" data-testid="prompt-replay-error" role="alert">
          <Warning aria-hidden="true" weight="fill" />
          {run.error}
        </div>
      )}

      <div className="prompt-replay-grid">
        <div className="prompt-replay-checkpoints" aria-label="Generated replay questions">
          {checkpointRows.map((checkpoint) => (
            <button
              key={checkpoint.id}
              className={`prompt-replay-checkpoint ${checkpoint.id === selectedCheckpointId ? "selected" : ""} ${checkpoint.selected ? "" : "muted-row"}`}
              data-testid="prompt-replay-checkpoint"
              type="button"
              onClick={() => onSelectCheckpoint(checkpoint.id)}
            >
              <span className="prompt-replay-checkpoint-index">#{checkpoint.ordinal}</span>
              <span>
                <strong>{formatCheckpointQuestionText(checkpoint)}</strong>
                <small>
                  {checkpoint.speaker} · {checkpoint.checkpointKind} · {checkpoint.wordCount} words ·{" "}
                  {formatDuration(checkpoint.durationMs)}
                </small>
              </span>
              {checkpoint.selected ? (
                <CheckCircle aria-label="Selected" weight="fill" />
              ) : (
                <Warning aria-label="No selection" weight="fill" />
              )}
            </button>
          ))}
          {checkpointRows.length === 0 && (
            <div className="prompt-replay-empty compact">
              {running
                ? "Waiting for the first generated question..."
                : cancelled
                  ? "Replay stopped before another checkpoint was generated."
                  : "No eligible answer-block checkpoints were found."}
            </div>
          )}
        </div>

        {selectedCheckpoint ? (
          <ReplayCheckpointDetail checkpoint={selectedCheckpoint} />
        ) : (
          <div className="prompt-replay-empty compact">
            {checkpointRows.length ? "Loading checkpoint detail..." : "Select a generated question to inspect details."}
          </div>
        )}
      </div>
    </section>
  );
}

function ReplayCheckpointDetail({ checkpoint }: { checkpoint: PromptReplayCheckpointDetail }) {
  return (
    <article className="prompt-replay-detail" data-testid="prompt-replay-detail">
      <header>
        <div>
          <p className="prompt-key">
            checkpoint {checkpoint.checkpointKind} · through {checkpoint.checkpointThroughTs}
          </p>
          <h2>{checkpoint.speaker}</h2>
        </div>
        <span className={`prompt-state ${checkpoint.selected ? "custom" : "default"}`}>
          {formatSelectionMethod(checkpoint.selectionMethod)}
        </span>
      </header>

      {checkpoint.selected ? (
        <section className="prompt-replay-selected" data-testid="prompt-replay-selected">
          <h3>Selected questions</h3>
          {(checkpoint.selectedSuggestions.length > 0 ? checkpoint.selectedSuggestions : [checkpoint.selected]).map(
            (suggestion, index) => (
              <div key={`${suggestion.signature}:${index}`} className="prompt-replay-selected-item">
                <p>{suggestion.prompt}</p>
                <small>{suggestion.rationale}</small>
              </div>
            ),
          )}
        </section>
      ) : (
        <div className="prompt-replay-empty compact">No candidate survived selection.</div>
      )}

      <ReplayTextBlock title="Current answer block" lines={checkpoint.currentBlockTranscript} />
      <ReplayTextBlock title="Conversation summary" lines={[checkpoint.conversationSummary || "(none)"]} />

      <section className="prompt-replay-card">
        <h3>Candidates</h3>
        <div className="prompt-replay-candidates">
          {checkpoint.candidates.map((candidate, index) => (
            <div
              key={`${candidate.signature ?? candidate.prompt}:${index}`}
              className={`prompt-replay-candidate ${checkpoint.selectedIndexes.includes(index) ? "selected" : ""}`}
            >
              <strong>{candidate.prompt}</strong>
              <span>{candidate.rationale}</span>
              <small>Evidence: {candidate.evidence.join(" / ")}</small>
            </div>
          ))}
          {checkpoint.candidates.length === 0 && <span className="muted">No candidates generated.</span>}
        </div>
      </section>

      {checkpoint.rejected.length > 0 && (
        <section className="prompt-replay-card">
          <h3>Rejected before selection</h3>
          <ul className="prompt-replay-rejections">
            {checkpoint.rejected.map((rejection, index) => (
              <li key={`${rejection.reason}:${index}`}>
                <strong>{rejection.reason}</strong>
                <span>{rejection.candidate.prompt}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {checkpoint.judgeResult && (
        <section className="prompt-replay-card">
          <h3>Judge result</h3>
          <pre>{JSON.stringify(checkpoint.judgeResult, null, 2)}</pre>
        </section>
      )}

      <details className="prompt-replay-card">
        <summary>Rendered LLM request</summary>
        <ReplayTextBlock title="System prompt" lines={[checkpoint.systemPrompt]} />
        <ReplayTextBlock title="User prompt" lines={[checkpoint.userPrompt]} />
      </details>
    </article>
  );
}

function Metric({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: string;
  tone?: "default" | "live" | "warn";
}) {
  return (
    <div className={`prompt-replay-metric ${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function formatCheckpointQuestionText(checkpoint: PromptReplayCheckpointSummary): string {
  if (!checkpoint.questionText) return "No question selected";
  const questions = checkpoint.questionText.split(/\r?\n/).filter(Boolean);
  if (questions.length <= 1) return checkpoint.questionText;
  return `${questions[0]} (+${questions.length - 1})`;
}

function ReplayTextBlock({ title, lines }: { title: string; lines: string[] }) {
  return (
    <section className="prompt-replay-card">
      <h3>{title}</h3>
      <pre>{lines.join("\n")}</pre>
    </section>
  );
}

function formatDuration(durationMs: number): string {
  return `${Math.round(durationMs / 1000)}s`;
}

function formatTranscriptCount(session: PromptReplaySessionSummary): string {
  const count = session.transcriptEntryCount ?? 0;
  const suffix = session.transcriptEntryCountIsApproximate ? "+" : "";
  return `${count}${suffix} transcript lines`;
}
